// Single source of truth for "where is this tool actually offered" — shared by the Tools tab
// (Enabled tools) and the Modules tab (Screens), so the two views can never classify the same
// agent differently. A tool is only ever "enabled" in the sense SAM's scope filter cares about
// when it is BOTH owned (in `agent.tools`) AND placed somewhere (core, global override, a
// module / card override, or a panel block that brings it) — see api-service/app/services/llm/page_tools.py and TOOL_SCOPE.md. Owned-but-
// unplaced tools are real bugs (invisible to the agent despite looking "enabled"); this module is
// what lets both tabs surface that instead of silently disagreeing.

export interface ToolPlacement {
  /** Passes the scope filter unconditionally whenever owned — CORE_TOOLS or admin global_tools. */
  isGlobal: boolean;
  /** Screen modules (slugs) whose own override explicitly lists this tool. */
  screens: string[];
}

export interface ScopeOverrides {
  global_tools: string[];
  screens: Record<string, string[]>;
  /** Core tools the admin took off the global list: still enabled on the agent, but placed nowhere until placed again. */
  core_opt_out?: string[];
}

/** A prompt block that brings tools while its condition holds (a panel block): those tools are placed at that card / panel. */
export interface BlockTools {
  tools?: string[];
  topic_cards?: string[];
}

/**
 * Classifies every owned tool name's placement. `ownedNames` should be every name actually
 * attached to the agent (`tools[].name || tools[].tool_name`) — a name placed in `overrides` but
 * not owned is a different bug (placed-but-not-owned) and intentionally left out of the result;
 * callers that need to flag *that* case should diff `overrides` against `ownedNames` themselves.
 */
export function computeToolPlacements(
  ownedNames: Iterable<string>,
  overrides: ScopeOverrides,
  coreTools: string[],
  blocks: BlockTools[] = [],
): Map<string, ToolPlacement> {
  const optedOut = new Set(overrides.core_opt_out ?? []);
  const globalSet = new Set([...coreTools.filter((n) => !optedOut.has(n)), ...overrides.global_tools]);
  const screensByName = new Map<string, string[]>();
  Object.entries(overrides.screens).forEach(([module, names]) => {
    names.forEach((n) => {
      const list = screensByName.get(n);
      if (list) list.push(module); else screensByName.set(n, [module]);
    });
  });

  // Tools a block brings are placed at the block's card / panel, so they are not "not placed".
  blocks.forEach((b) => {
    const where = (b.topic_cards ?? []).find(Boolean) ?? 'screen block';
    (b.tools ?? []).forEach((n) => {
      const list = screensByName.get(n);
      if (list) { if (!list.includes(where)) list.push(where); } else screensByName.set(n, [where]);
    });
  });

  const result = new Map<string, ToolPlacement>();
  for (const name of ownedNames) {
    result.set(name, {
      isGlobal: globalSet.has(name),
      screens: screensByName.get(name) ?? [],
    });
  }
  return result;
}

/** True when a tool is owned but placed nowhere — attached yet invisible to the agent in scoped mode. */
export function isUnplaced(placement: ToolPlacement | undefined): boolean {
  return !placement || (!placement.isGlobal && placement.screens.length === 0);
}

export function sortToolNames(names: string[]): string[] {
  return [...names].sort((a, b) => a.localeCompare(b));
}
